<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useStore } from 'dashboard/composables/store';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import { createContactSearcher } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import ProductBarcodeScannerDrawer from './ProductBarcodeScannerDrawer.vue';
import { useShopifyProductsState } from './useShopifyProductsState';

/**
 * @typedef {Object} AlertDraft
 * @property {string} productId
 * @property {string} variantId
 * @property {string} customerId
 * @property {string[]} preferredChannels
 */

const { t } = useI18n();
const store = useStore();
const {
  searchQuery,
  selectedStoreId,
  channels,
  storeLocations,
  filteredProducts,
  waitlistByProduct,
  createAlert,
  fetchProducts,
  loadMoreProducts,
  isLoadingProducts,
  isLoadingMoreProducts,
  productLoadError,
  productPaginationError,
  reachedEndOfProducts,
} = useShopifyProductsState();

const searchContacts = createContactSearcher({ requireContactMethod: false });
const productDrawer = ref(null);
const alertDraft = ref(null);
const customerQuery = ref('');
const contactSearchResults = ref([]);
const contactSearchError = ref(false);
const isContactSearchLoading = ref(false);
const contactSearchRequestId = ref(0);
const isWaitlistOpen = ref(false);
const isScannerOpen = ref(false);
const successMessage = ref('');
const editingChannelValueId = ref('');
const channelValueDrafts = ref({});
const savingChannelValueId = ref('');
const channelValueSaveErrorId = ref('');
const textSeparator = ' · ';
const detailSeparator = ': ';
const scrollLoadThreshold = 240;

const selectedVariant = computed(
  () =>
    productDrawer.value?.variants.find(
      variant => variant.id === alertDraft.value?.variantId
    ) || null
);
const selectedCustomer = computed(() => alertDraft.value?.customer || null);
const hasCustomerQuery = computed(() => customerQuery.value.trim().length >= 2);
const canConfirmAlert = computed(() =>
  Boolean(
    alertDraft.value?.productId &&
      alertDraft.value?.variantId &&
      selectedCustomer.value &&
      alertDraft.value?.preferredChannels?.length
  )
);

/**
 * Formats a currency amount using the same compact display for every product
 * row and drawer summary.
 *
 * @param {number} value Amount in product currency.
 * @param {string} currency ISO currency code.
 * @returns {string} Formatted currency label.
 */
function formatCurrency(value, currency) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Returns the product price or variant price range shown in compact rows.
 *
 * @param {Object} product Product fixture.
 * @returns {string} Formatted product price label.
 */
function productPriceLabel(product) {
  const prices = product.variants?.length
    ? product.variants
        .map(variant => variant.price || product.price)
        .filter(price => Number.isFinite(Number(price)))
    : [product.price];
  if (!prices.length) return formatCurrency(0, product.currency);

  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  if (minPrice !== maxPrice) {
    return `${formatCurrency(minPrice, product.currency)} - ${formatCurrency(
      maxPrice,
      product.currency
    )}`;
  }

  return formatCurrency(minPrice, product.currency);
}

/**
 * Returns a compact stock summary for a product based on normalized Shopify
 * variant inventory values.
 *
 * @param {Object} product Product loaded from the Shopify cache.
 * @returns {string} Localized stock summary label.
 */
function productStockSummaryLabel(product) {
  const stocks = product.variants.map(variant => variant.stock);
  if (!stocks.length) return t('PRODUCTS.STOCK_SUMMARY.unknown');
  if (stocks.every(stock => stock === 0)) {
    return t('PRODUCTS.STOCK_SUMMARY.out_of_stock');
  }
  if (stocks.some(stock => stock === 0)) {
    return t('PRODUCTS.STOCK_SUMMARY.partial_out_of_stock');
  }
  if (stocks.some(stock => stock === 1)) {
    return t('PRODUCTS.STOCK_SUMMARY.low_stock');
  }
  return t('PRODUCTS.STOCK_SUMMARY.available');
}

/**
 * Checks whether a Shopify variant has a customer-meaningful option title.
 *
 * @param {Object} variant Product variant loaded from Shopify cache.
 * @returns {boolean} Whether the variant should be shown as a separate row.
 */
function hasMeaningfulVariantTitle(variant) {
  return (
    variant &&
    String(variant.title || '')
      .trim()
      .toLowerCase() !== 'default title'
  );
}

/**
 * Returns variants that should be shown as actual selectable variants.
 *
 * @param {Object} product Product loaded from Shopify cache.
 * @returns {Array<Object>} Displayable variants.
 */
function displayableVariants(product) {
  const variants = product.variants || [];
  if (variants.length <= 1 && !hasMeaningfulVariantTitle(variants[0])) {
    return [];
  }

  return variants.filter(hasMeaningfulVariantTitle);
}

/**
 * Returns the visible variant count for products with real variant choices.
 *
 * @param {Object} product Product loaded from Shopify cache.
 * @returns {number} Number of displayable variants.
 */
function displayableVariantCount(product) {
  return displayableVariants(product).length;
}

/**
 * Returns the visible label for a synced Shopify location option.
 *
 * @param {Object} storeLocation Store selector option.
 * @returns {string} Store name.
 */
function storeLocationLabel(storeLocation) {
  return storeLocation.isAll
    ? t('PRODUCTS.STORE_FILTER.ALL_LOCATIONS')
    : storeLocation.name;
}

/**
 * Normalizes contact search results so the product alert drawer can use the
 * same snapshot shape whether data comes from contacts API or future mocks.
 *
 * @param {Object} contact Contact search result.
 * @returns {Object} Contact snapshot.
 */
function normalizeContact(contact) {
  const phone = contact.phoneNumber || contact.phone || '';

  return {
    id: String(contact.id),
    contactId: contact.id,
    name: contact.name || contact.email || phone || '',
    email: contact.email || '',
    phone,
    thumbnail: contact.thumbnail || '',
    consent: contact.consent || {
      whatsapp: 'unknown',
      sms: 'unknown',
      email: 'unknown',
    },
  };
}

/**
 * Returns the localized stock label without dynamic i18n keys.
 *
 * @param {number} stock Variant stock count.
 * @returns {string} Localized stock label.
 */
function stockLabel(stock) {
  if (stock === 0) return t('PRODUCTS.STOCK.OUT_OF_STOCK');
  if (stock === 1) return t('PRODUCTS.STOCK.LOW_STOCK');
  return t('PRODUCTS.STOCK.AVAILABLE');
}

/**
 * Returns the compact variant reference line without empty or duplicate values.
 *
 * @param {Object} variant Product variant fixture.
 * @returns {string} Searchable reference label.
 */
function variantReferenceLabel(variant) {
  return [variant.sku, variant.barcode]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(textSeparator);
}

/**
 * Returns a stable icon for the preferred alert channel.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Icon class.
 */
function channelIcon(channelId) {
  return {
    whatsapp: 'i-ri-whatsapp-fill',
    sms: 'i-lucide-message-square-text',
    email: 'i-lucide-mail',
  }[channelId];
}

/**
 * Returns the rounded icon treatment used by channel choice cards.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Tailwind class list.
 */
function channelIconBadgeClass(channelId) {
  if (channelId === 'whatsapp') return 'bg-[#22c55e1a] text-[#16a34a]';
  if (channelId === 'email') return 'bg-n-blue-3 text-n-blue-11';
  return 'bg-n-slate-4 text-n-slate-11';
}

/**
 * Returns the localized channel label without dynamic i18n keys.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Localized channel label.
 */
function channelLabel(channelId) {
  if (channelId === 'whatsapp') return t('PRODUCTS.CHANNELS.WHATSAPP');
  if (channelId === 'sms') return t('PRODUCTS.CHANNELS.SMS');
  return t('PRODUCTS.CHANNELS.EMAIL');
}

/**
 * Returns the localized contact detail label shown under a channel.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Localized label.
 */
function channelDetailLabel(channelId) {
  if (channelId === 'email') return t('PRODUCTS.ALERT_DRAWER.EMAIL_DETAIL');
  return t('PRODUCTS.ALERT_DRAWER.PHONE_DETAIL');
}

/**
 * Returns the current selected contact value used by a channel.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Email or phone value used for this channel.
 */
function channelContactValue(channelId) {
  if (!selectedCustomer.value) return '';
  if (channelId === 'email') return selectedCustomer.value.email || '';
  return selectedCustomer.value.phone || '';
}

/**
 * Returns the missing detail text for a channel without contact data.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Localized missing detail text.
 */
function missingChannelValueLabel(channelId) {
  if (channelId === 'email') return t('PRODUCTS.ALERT_DRAWER.MISSING_EMAIL');
  return t('PRODUCTS.ALERT_DRAWER.MISSING_PHONE');
}

/**
 * Returns the inline input type for a missing channel detail.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} HTML input type.
 */
function channelValueInputType(channelId) {
  if (channelId === 'email') return 'email';
  return 'tel';
}

/**
 * Checks whether a channel detail uses the shared phone number input.
 *
 * @param {string} channelId Notification channel id.
 * @returns {boolean} Whether the channel detail is a phone number.
 */
function isPhoneChannel(channelId) {
  return channelId !== 'email';
}

/**
 * Returns the inline input placeholder for a missing channel detail.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Localized placeholder.
 */
function channelValuePlaceholder(channelId) {
  if (channelId === 'email') {
    return t('PRODUCTS.ALERT_DRAWER.EMAIL_PLACEHOLDER');
  }

  return t('PRODUCTS.ALERT_DRAWER.PHONE_PLACEHOLDER');
}

/**
 * Returns a localized, explicit consent label for channel tags.
 *
 * @param {string} consent Consent status.
 * @returns {string} Localized consent label.
 */
function consentLabel(consent) {
  if (consent === 'allowed') return t('PRODUCTS.CONSENT.allowed');
  if (consent === 'subscribed') return t('PRODUCTS.CONSENT.subscribed');
  if (consent === 'not_subscribed') return t('PRODUCTS.CONSENT.not_subscribed');
  return t('PRODUCTS.CONSENT.unknown');
}

/**
 * Returns visual treatment for a compact consent tag.
 *
 * @param {string} consent Consent status.
 * @returns {string} Tailwind class list.
 */
function consentTagClass(consent) {
  if (consent === 'allowed' || consent === 'subscribed') {
    return 'border-n-teal-5 bg-n-teal-3 text-n-teal-11';
  }

  if (consent === 'not_subscribed') {
    return 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11';
  }

  return 'border-n-amber-5 bg-n-amber-3 text-n-amber-11';
}

/**
 * Checks whether an alert draft channel is selected.
 *
 * @param {string} channelId Notification channel id.
 * @returns {boolean} Whether the draft includes this channel.
 */
function isChannelSelected(channelId) {
  return Boolean(alertDraft.value?.preferredChannels?.includes(channelId));
}

/**
 * Returns the border and background treatment for a channel choice card.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Tailwind class list.
 */
function channelCardClass(channelId) {
  if (isChannelSelected(channelId)) {
    return 'border-n-brand bg-n-brand/5 shadow-sm';
  }

  if (channelId === 'whatsapp') {
    return 'border-n-weak bg-n-solid-1 hover:border-[#22c55e80] hover:bg-[#22c55e0d]';
  }

  if (channelId === 'email') {
    return 'border-n-weak bg-n-solid-1 hover:border-n-blue-4 hover:bg-n-blue-2';
  }

  return 'border-n-weak bg-n-solid-1 hover:border-n-slate-7 hover:bg-n-alpha-2';
}

/**
 * Checks whether a missing channel detail editor is open.
 *
 * @param {string} channelId Notification channel id.
 * @returns {boolean} Whether the inline editor is visible.
 */
function isChannelValueEditorOpen(channelId) {
  return editingChannelValueId.value === channelId;
}

/**
 * Returns the draft value currently typed for a missing channel detail.
 *
 * @param {string} channelId Notification channel id.
 * @returns {string} Unsaved channel detail draft.
 */
function channelValueDraft(channelId) {
  return channelValueDrafts.value[channelId] || '';
}

/**
 * Checks whether a missing channel detail draft can be saved.
 *
 * @param {string} channelId Notification channel id.
 * @returns {boolean} Whether the draft contains a value.
 */
function canSaveChannelValue(channelId) {
  return channelValueDraft(channelId).trim().length > 0;
}

/**
 * Checks whether a channel detail is currently being saved to the contact.
 *
 * @param {string} channelId Notification channel id.
 * @returns {boolean} Whether the save action is pending.
 */
function isChannelValueSaving(channelId) {
  return savingChannelValueId.value === channelId;
}

/**
 * Opens the inline editor for a missing customer channel detail.
 *
 * @param {string} channelId Notification channel id.
 * @returns {void}
 */
function openChannelValueEditor(channelId) {
  editingChannelValueId.value = channelId;
  channelValueSaveErrorId.value = '';
  channelValueDrafts.value = {
    ...channelValueDrafts.value,
    [channelId]: channelContactValue(channelId),
  };
}

/**
 * Updates the unsaved draft for a missing customer channel detail.
 *
 * @param {string} channelId Notification channel id.
 * @param {string} value New draft value.
 * @returns {void}
 */
function updateChannelValueDraft(channelId, value) {
  channelValueDrafts.value = {
    ...channelValueDrafts.value,
    [channelId]: String(value || ''),
  };
}

/**
 * Marks a selected contact's channel consent as granted in the local mock only
 * once the seller confirms the back-in-stock alert.
 *
 * @param {string} channelId Notification channel id.
 * @returns {void}
 */
function validateChannelConsent(channelId) {
  if (!selectedCustomer.value) return;

  selectedCustomer.value.consent = {
    ...selectedCustomer.value.consent,
    [channelId]: 'subscribed',
  };
}

/**
 * Toggles a channel in the alert draft while allowing multi-channel selection.
 *
 * @param {string} channelId Notification channel id.
 * @returns {void}
 */
function toggleChannel(channelId) {
  if (!alertDraft.value) return;

  const selectedChannels = alertDraft.value.preferredChannels || [];
  if (selectedChannels.includes(channelId)) {
    alertDraft.value.preferredChannels = selectedChannels.filter(
      selectedChannel => selectedChannel !== channelId
    );
    return;
  }

  alertDraft.value.preferredChannels = [...selectedChannels, channelId];
}

/**
 * Updates the selected contact snapshot with an inline channel detail.
 *
 * @param {string} channelId Notification channel id.
 * @param {string} value New contact detail value.
 * @returns {void}
 */
function updateSelectedCustomerChannelValue(channelId, value) {
  if (!selectedCustomer.value) return;

  const normalizedValue = String(value || '').trim();
  if (channelId === 'email') {
    selectedCustomer.value.email = normalizedValue;
    return;
  }

  selectedCustomer.value.phone = normalizedValue;
}

/**
 * Saves a missing channel detail draft through the real contact update action,
 * then mirrors the accepted value in the local alert snapshot.
 *
 * @param {string} channelId Notification channel id.
 * @returns {Promise<void>}
 */
async function saveSelectedCustomerChannelValue(channelId) {
  if (!selectedCustomer.value || !canSaveChannelValue(channelId)) return;

  const contactId =
    selectedCustomer.value.contactId || selectedCustomer.value.id;
  const normalizedValue = channelValueDraft(channelId).trim();
  const updatePayload =
    channelId === 'email'
      ? { id: contactId, email: normalizedValue }
      : { id: contactId, phoneNumber: normalizedValue };

  savingChannelValueId.value = channelId;
  channelValueSaveErrorId.value = '';

  try {
    await store.dispatch('contacts/update', updatePayload);
    updateSelectedCustomerChannelValue(channelId, normalizedValue);
    editingChannelValueId.value = '';
    channelValueDrafts.value = {
      ...channelValueDrafts.value,
      [channelId]: '',
    };
  } catch {
    channelValueSaveErrorId.value = channelId;
  } finally {
    savingChannelValueId.value = '';
  }
}

/**
 * Returns a compact list of channels stored on a waitlist alert.
 *
 * @param {Object} alert Waitlist alert.
 * @returns {string[]} Notification channel ids.
 */
function alertChannels(alert) {
  return alert.preferredChannels?.length
    ? alert.preferredChannels
    : [alert.preferredChannel || 'email'];
}

/**
 * Returns a localized, comma-separated channel label for a waitlist alert.
 *
 * @param {Object} alert Waitlist alert.
 * @returns {string} Localized channel labels.
 */
function alertChannelLabel(alert) {
  return alertChannels(alert).map(channelLabel).join(', ');
}

/**
 * Builds compact initials for selected contact affordance.
 *
 * @param {string} name Contact display name.
 * @returns {string} One or two letter initials.
 */
function contactInitials(name) {
  return String(name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
}

/**
 * Searches existing Glovely contacts from the account contact API.
 *
 * @returns {Promise<void>}
 */
async function runContactSearch() {
  const query = customerQuery.value.trim();
  contactSearchRequestId.value += 1;
  const requestId = contactSearchRequestId.value;

  contactSearchError.value = false;
  if (selectedCustomer.value && query === selectedCustomer.value.name) {
    contactSearchResults.value = [];
    isContactSearchLoading.value = false;
    return;
  }

  if (selectedCustomer.value && query !== selectedCustomer.value.name) {
    alertDraft.value.customerId = '';
    alertDraft.value.customer = null;
  }

  if (query.length < 2) {
    contactSearchResults.value = [];
    isContactSearchLoading.value = false;
    return;
  }

  isContactSearchLoading.value = true;

  try {
    const results = await searchContacts(query);
    if (requestId !== contactSearchRequestId.value || results === null) return;

    contactSearchResults.value = results.map(normalizeContact);
  } catch {
    if (requestId !== contactSearchRequestId.value) return;

    contactSearchError.value = true;
    contactSearchResults.value = [];
  } finally {
    if (requestId === contactSearchRequestId.value) {
      isContactSearchLoading.value = false;
    }
  }
}

/**
 * Opens the product detail drawer.
 *
 * @param {Object} product Product fixture.
 * @returns {void}
 */
function openProduct(product) {
  productDrawer.value = product;
}

/**
 * Opens the camera barcode scanner drawer.
 *
 * @returns {void}
 */
function handleScan() {
  successMessage.value = '';
  isScannerOpen.value = true;
}

/**
 * Applies a scanned barcode to product search and opens the first matching
 * product so the seller can continue directly in the product flow.
 *
 * @param {string} barcode Barcode detected by the camera scanner.
 * @returns {void}
 */
async function handleBarcodeDetected(barcode) {
  searchQuery.value = barcode;
  isScannerOpen.value = false;
  await fetchProducts();
  if (filteredProducts.value[0]) openProduct(filteredProducts.value[0]);
}

/**
 * Loads the next Shopify product page as the seller reaches the bottom of the
 * current catalog results.
 *
 * @param {Event} event Scroll event from the product page body.
 * @returns {void}
 */
function handleProductsScroll(event) {
  const target = event.currentTarget;
  if (!target) return;

  const distanceToBottom =
    target.scrollHeight - target.scrollTop - target.clientHeight;
  if (distanceToBottom <= scrollLoadThreshold) {
    loadMoreProducts();
  }
}

/**
 * Closes every product-related drawer.
 *
 * @returns {void}
 */
function closeDrawers() {
  productDrawer.value = null;
  alertDraft.value = null;
  isWaitlistOpen.value = false;
  isScannerOpen.value = false;
  customerQuery.value = '';
  editingChannelValueId.value = '';
  channelValueDrafts.value = {};
  savingChannelValueId.value = '';
  channelValueSaveErrorId.value = '';
}

/**
 * Starts a mocked back-in-stock alert from an out-of-stock variant.
 *
 * @param {Object} variant Product variant fixture.
 * @returns {void}
 */
function openAlertDrawer(variant) {
  if (!productDrawer.value || variant.stock > 0) return;

  successMessage.value = '';
  customerQuery.value = '';
  alertDraft.value = {
    productId: productDrawer.value.id,
    variantId: variant.id,
    customerId: '',
    customer: null,
    preferredChannels: [],
  };
  editingChannelValueId.value = '';
  channelValueDrafts.value = {};
  savingChannelValueId.value = '';
  channelValueSaveErrorId.value = '';
}

/**
 * Selects a mocked customer for the alert draft.
 *
 * @param {Object} customer Customer fixture.
 * @returns {void}
 */
function selectCustomer(customer) {
  if (!alertDraft.value) return;

  alertDraft.value.customerId = customer.id;
  alertDraft.value.customer = customer;
  contactSearchResults.value = [];
  customerQuery.value = customer.name;
  editingChannelValueId.value = '';
  channelValueDrafts.value = {};
  savingChannelValueId.value = '';
  channelValueSaveErrorId.value = '';
}

/**
 * Clears the selected customer and returns to the contact search state.
 *
 * @returns {void}
 */
function deselectCustomer() {
  if (!alertDraft.value) return;

  alertDraft.value.customerId = '';
  alertDraft.value.customer = null;
  customerQuery.value = '';
  contactSearchResults.value = [];
  editingChannelValueId.value = '';
  channelValueDrafts.value = {};
  savingChannelValueId.value = '';
  channelValueSaveErrorId.value = '';
}

/**
 * Creates a local-only back-in-stock alert and keeps the seller in the product
 * context so they can continue the live interaction.
 *
 * @returns {void}
 */
function confirmAlert() {
  if (!alertDraft.value || !canConfirmAlert.value) return;

  alertDraft.value.preferredChannels.forEach(validateChannelConsent);
  createAlert(alertDraft.value);
  alertDraft.value = null;
  customerQuery.value = '';
  editingChannelValueId.value = '';
  channelValueDrafts.value = {};
  savingChannelValueId.value = '';
  channelValueSaveErrorId.value = '';
  successMessage.value = t('PRODUCTS.ALERT_DRAWER.CREATED');
}

watch(customerQuery, runContactSearch);
</script>

<template>
  <section class="flex w-full h-full overflow-hidden bg-n-solid-1">
    <div class="flex flex-col w-full h-full">
      <header class="sticky top-0 z-10 px-4 bg-n-solid-1 md:px-6">
        <div class="w-full py-4 mx-auto max-w-5xl md:py-5">
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <h1 class="text-xl font-medium truncate text-n-slate-12">
                {{ t('PRODUCTS.TITLE') }}
              </h1>
            </div>

            <Button
              data-testid="waitlist-open"
              icon="i-lucide-list-filter"
              color="slate"
              size="sm"
              variant="faded"
              :label="t('PRODUCTS.WAITLIST.SHORT_TITLE')"
              @click="isWaitlistOpen = true"
            />
          </div>

          <label class="mt-4 block text-sm">
            <span class="relative block">
              <Icon
                icon="i-lucide-store"
                class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-11"
              />
              <select
                v-model="selectedStoreId"
                data-testid="products-store-filter"
                class="reset-base m-0 h-10 w-full appearance-none rounded-lg border-0 bg-n-slate-9/10 py-0 pl-9 pr-9 text-sm font-medium text-n-slate-12 outline outline-1 outline-transparent transition-colors [-webkit-appearance:none] [background-image:none] hover:bg-n-slate-9/20 focus:outline-n-brand"
                :aria-label="t('PRODUCTS.STORE_FILTER.LABEL')"
              >
                <option
                  v-for="storeLocation in storeLocations"
                  :key="storeLocation.id"
                  :value="storeLocation.id"
                >
                  {{ storeLocationLabel(storeLocation) }}
                </option>
              </select>
              <Icon
                icon="i-lucide-chevron-down"
                class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
              />
            </span>
          </label>

          <div class="mt-3 flex items-center gap-2">
            <div data-testid="products-search-shell" class="flex-1 min-w-0">
              <Input
                v-model="searchQuery"
                data-testid="products-search-input"
                type="search"
                size="md"
                :placeholder="t('PRODUCTS.SEARCH_PLACEHOLDER')"
                :custom-input-class="[
                  'h-10 [&:not(.focus)]:!border-transparent bg-n-slate-9/10 ltr:!pl-9 !py-2 rtl:!pr-9',
                ]"
              >
                <template #prefix>
                  <Icon
                    icon="i-lucide-search"
                    class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-3 rtl:right-3"
                  />
                </template>
              </Input>
            </div>
            <Button
              data-testid="products-scan-button"
              icon="i-lucide-scan-line"
              color="slate"
              size="md"
              variant="faded"
              class="!h-10 !w-10"
              :aria-label="t('PRODUCTS.SCAN')"
              :title="t('PRODUCTS.SCAN')"
              @click="handleScan"
            />
          </div>
        </div>
      </header>

      <main
        class="flex-1 overflow-y-auto px-3 md:px-6"
        @scroll="handleProductsScroll"
      >
        <div class="w-full mx-auto max-w-5xl">
          <p
            v-if="successMessage"
            class="mx-1 mb-2 rounded-md bg-n-teal-3 px-3 py-2 text-sm text-n-teal-11 md:mx-0"
          >
            {{ successMessage }}
          </p>

          <div class="flex flex-col">
            <div
              v-if="isLoadingProducts"
              data-testid="products-loading"
              class="px-3 py-12 text-sm text-center text-n-slate-11"
            >
              {{ t('PRODUCTS.LOADING') }}
            </div>
            <div
              v-else-if="productLoadError"
              data-testid="products-load-error"
              class="px-3 py-12 text-sm text-center text-n-ruby-11"
            >
              {{ t('PRODUCTS.LOAD_ERROR') }}
            </div>
            <template v-else>
              <button
                v-for="product in filteredProducts"
                :key="product.id"
                :data-testid="`product-row-${product.id}`"
                type="button"
                class="relative mx-1 flex items-center gap-3 border-b border-n-weak/50 px-1 py-3.5 text-left transition-colors last:border-b-0 hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-n-brand dark:border-n-weak/40 md:mx-0 md:px-2 md:py-4"
                @click="openProduct(product)"
              >
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="product.title"
                  class="size-10 shrink-0 rounded-lg object-cover bg-n-alpha-2"
                />
                <span
                  v-else
                  class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-10"
                >
                  <Icon icon="i-lucide-image" class="size-4" />
                </span>

                <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span class="flex min-w-0 items-center gap-2">
                    <span class="truncate text-sm font-medium text-n-slate-12">
                      {{ product.title }}
                    </span>
                    <span
                      v-if="product.shopifyStatus"
                      class="shrink-0 rounded-full bg-n-alpha-2 px-1.5 text-[10px] font-medium uppercase leading-4 text-n-slate-10"
                    >
                      {{ product.shopifyStatus }}
                    </span>
                  </span>
                  <span
                    class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5"
                  >
                    <span class="text-xs text-n-slate-10">
                      {{ t('PRODUCTS.REFERENCE_LABEL') }} {{ product.sku }}
                    </span>
                    <span class="h-3 w-px shrink-0 rounded bg-n-slate-6" />
                    <span class="text-xs text-n-slate-10">
                      {{ productPriceLabel(product) }}
                    </span>
                    <span class="h-3 w-px shrink-0 rounded bg-n-slate-6" />
                    <span
                      v-if="displayableVariantCount(product)"
                      class="text-xs text-n-slate-10"
                    >
                      {{
                        t('PRODUCTS.VARIANT_COUNT', {
                          count: displayableVariantCount(product),
                        })
                      }}
                    </span>
                    <span
                      v-if="displayableVariantCount(product)"
                      class="h-3 w-px shrink-0 rounded bg-n-slate-6"
                    />
                    <span class="text-xs text-n-slate-10">
                      {{ productStockSummaryLabel(product) }}
                    </span>
                    <template v-if="product.productType">
                      <span class="h-3 w-px shrink-0 rounded bg-n-slate-6" />
                      <span class="text-xs text-n-slate-10">
                        {{ product.productType }}
                      </span>
                    </template>
                  </span>
                </span>
              </button>
              <div
                v-if="isLoadingMoreProducts"
                data-testid="products-loading-more"
                class="px-3 py-5 text-sm text-center text-n-slate-11"
              >
                {{ t('PRODUCTS.LOADING_MORE') }}
              </div>
              <div
                v-else-if="productPaginationError"
                data-testid="products-pagination-error"
                class="px-3 py-5 text-sm text-center text-n-ruby-11"
              >
                {{ t('PRODUCTS.LOAD_MORE_ERROR') }}
              </div>
              <div
                v-else-if="reachedEndOfProducts"
                data-testid="products-end-of-list"
                class="px-3 py-5 text-sm text-center text-n-slate-10"
              >
                {{ t('PRODUCTS.END_OF_LIST') }}
              </div>
            </template>
          </div>

          <p
            v-if="
              !isLoadingProducts &&
              !productLoadError &&
              !filteredProducts.length
            "
            class="px-3 py-12 text-sm text-center text-n-slate-11"
          >
            {{ t('PRODUCTS.EMPTY') }}
          </p>
        </div>
      </main>
    </div>

    <div
      v-if="productDrawer || alertDraft || isWaitlistOpen || isScannerOpen"
      class="fixed inset-0 z-[60] flex items-end justify-center bg-n-alpha-black1 backdrop-blur-[4px] lg:items-stretch lg:justify-end"
      @click.self="closeDrawers"
    >
      <ProductBarcodeScannerDrawer
        v-if="isScannerOpen"
        @close="isScannerOpen = false"
        @detected="handleBarcodeDetected"
      />

      <section
        v-if="productDrawer && !alertDraft && !isWaitlistOpen && !isScannerOpen"
        data-testid="product-detail-drawer"
        class="flex h-[86vh] w-full flex-col overflow-hidden rounded-t-2xl border border-n-weak bg-n-solid-1 shadow-xl lg:h-full lg:max-w-xl lg:rounded-none"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-n-weak px-4 py-4"
        >
          <div class="min-w-0">
            <h2 class="truncate text-base font-semibold text-n-slate-12">
              {{ productDrawer.title }}
            </h2>
            <p class="truncate text-xs text-n-slate-10">
              {{
                [
                  productDrawer.vendor,
                  productDrawer.productType,
                  productDrawer.sku,
                ]
                  .filter(Boolean)
                  .join(textSeparator)
              }}
            </p>
          </div>
          <Button
            icon="i-lucide-x"
            color="slate"
            size="sm"
            variant="ghost"
            class="!rounded-full bg-n-alpha-2"
            @click="closeDrawers"
          />
        </header>

        <div class="flex-1 overflow-y-auto px-4 py-4">
          <div class="flex items-center gap-3">
            <img
              v-if="productDrawer.image"
              :src="productDrawer.image"
              :alt="productDrawer.title"
              class="size-16 rounded-lg object-cover bg-n-alpha-2"
            />
            <span
              v-else
              class="flex size-16 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-10"
            >
              <Icon icon="i-lucide-image" class="size-5" />
            </span>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12">
                {{ productPriceLabel(productDrawer) }}
              </p>
              <p class="text-xs text-n-slate-10">
                <template v-if="displayableVariantCount(productDrawer)">
                  {{
                    t('PRODUCTS.VARIANT_COUNT', {
                      count: displayableVariantCount(productDrawer),
                    })
                  }}
                </template>
                <template v-else>
                  {{ productDrawer.sku }}
                </template>
              </p>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ productStockSummaryLabel(productDrawer) }}
              </p>
            </div>
          </div>

          <dl
            class="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg bg-n-alpha-1 p-3 text-xs"
          >
            <div>
              <dt class="text-n-slate-10">
                {{ t('PRODUCTS.DETAILS.STATUS') }}
              </dt>
              <dd class="mt-0.5 font-medium text-n-slate-12">
                {{
                  productDrawer.shopifyStatus || t('PRODUCTS.DETAILS.UNKNOWN')
                }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">{{ t('PRODUCTS.DETAILS.TYPE') }}</dt>
              <dd class="mt-0.5 font-medium text-n-slate-12">
                {{ productDrawer.productType || t('PRODUCTS.DETAILS.UNKNOWN') }}
              </dd>
            </div>
            <div v-if="productDrawer.shopifyAdminUrl">
              <dt class="text-n-slate-10">
                {{ t('PRODUCTS.DETAILS.SHOPIFY_ADMIN') }}
              </dt>
              <dd class="mt-0.5">
                <a
                  :href="productDrawer.shopifyAdminUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium text-n-blue-11 hover:underline"
                >
                  {{ t('PRODUCTS.DETAILS.OPEN_IN_SHOPIFY') }}
                </a>
              </dd>
            </div>
            <div v-if="productDrawer.sourceUrl">
              <dt class="text-n-slate-10">
                {{ t('PRODUCTS.DETAILS.ONLINE') }}
              </dt>
              <dd class="mt-0.5">
                <a
                  :href="productDrawer.sourceUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium text-n-blue-11 hover:underline"
                >
                  {{ t('PRODUCTS.DETAILS.OPEN_ONLINE') }}
                </a>
              </dd>
            </div>
          </dl>

          <div
            v-if="displayableVariantCount(productDrawer)"
            class="mt-5 divide-y divide-n-weak"
          >
            <div
              v-for="variant in displayableVariants(productDrawer)"
              :key="variant.id"
              class="flex items-center justify-between gap-3 py-3"
            >
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-n-slate-12">
                  {{ variant.title }}
                </p>
                <p class="truncate text-xs text-n-slate-10">
                  {{ variantReferenceLabel(variant) }}
                </p>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ formatCurrency(variant.price, variant.currency) }}
                </p>
                <p
                  class="mt-1 text-xs"
                  :class="
                    variant.stock === 0 ? 'text-n-ruby-10' : 'text-n-slate-11'
                  "
                >
                  {{ stockLabel(variant.stock) }}
                </p>
              </div>

              <Button
                v-if="variant.stock === 0"
                :data-testid="`variant-alert-${variant.id}`"
                icon="i-lucide-bell-plus"
                color="blue"
                size="sm"
                :label="t('PRODUCTS.CREATE_ALERT')"
                @click="openAlertDrawer(variant)"
              />
              <span v-else class="shrink-0 text-sm text-n-slate-11">
                {{ t('PRODUCTS.STOCK_COUNT', { count: variant.stock }) }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="alertDraft && !isScannerOpen"
        data-testid="alert-drawer"
        class="flex h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl border border-n-weak bg-n-solid-1 shadow-xl lg:h-full lg:max-w-md lg:rounded-none"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-n-weak px-4 py-4"
        >
          <div class="min-w-0">
            <h2 class="truncate text-base font-semibold text-n-slate-12">
              {{ t('PRODUCTS.ALERT_DRAWER.TITLE') }}
            </h2>
            <p class="truncate text-xs text-n-slate-10">
              {{ productDrawer?.title }}{{ textSeparator
              }}{{ selectedVariant?.title }}
            </p>
          </div>
          <Button
            icon="i-lucide-x"
            color="slate"
            size="sm"
            variant="ghost"
            class="!rounded-full bg-n-alpha-2"
            @click="alertDraft = null"
          />
        </header>

        <div class="flex-1 overflow-y-auto px-4 py-4">
          <section class="grid gap-3 text-sm">
            <h3 class="font-medium text-n-slate-12">
              {{ t('PRODUCTS.ALERT_DRAWER.CUSTOMER') }}
            </h3>
            <input
              v-if="!selectedCustomer"
              v-model="customerQuery"
              data-testid="customer-search-input"
              type="search"
              class="m-0 h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="t('PRODUCTS.ALERT_DRAWER.CUSTOMER_PLACEHOLDER')"
            />

            <div
              v-else
              data-testid="selected-customer"
              class="flex items-center gap-3 px-1 py-1"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-xs font-semibold text-n-blue-11"
              >
                {{ contactInitials(selectedCustomer.name) }}
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12"
                >
                  {{ selectedCustomer.name }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{
                    [selectedCustomer.email, selectedCustomer.phone]
                      .filter(Boolean)
                      .join(textSeparator)
                  }}
                </span>
              </span>
              <button
                data-testid="customer-deselect"
                type="button"
                class="flex size-8 shrink-0 items-center justify-center rounded-full text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-ruby-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
                :aria-label="t('PRODUCTS.ALERT_DRAWER.DESELECT_CUSTOMER')"
                :title="t('PRODUCTS.ALERT_DRAWER.DESELECT_CUSTOMER')"
                @click="deselectCustomer"
              >
                <Icon icon="i-lucide-x" class="size-4" />
              </button>
            </div>
          </section>

          <div
            v-if="isContactSearchLoading"
            class="mt-3 px-1 py-3 text-sm text-n-slate-11"
          >
            {{ t('PRODUCTS.ALERT_DRAWER.CONTACT_SEARCH_LOADING') }}
          </div>

          <div
            v-else-if="
              !selectedCustomer &&
              hasCustomerQuery &&
              contactSearchResults.length
            "
            data-testid="contact-search-results"
            class="mt-2 divide-y divide-n-weak/60"
          >
            <button
              v-for="customer in contactSearchResults"
              :key="customer.id"
              :data-testid="`customer-option-${customer.id}`"
              type="button"
              class="flex w-full items-center gap-3 px-1 py-3 text-left transition-colors hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-n-brand"
              :class="
                alertDraft.customerId === customer.id ? 'bg-n-alpha-1' : ''
              "
              @click="selectCustomer(customer)"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-xs font-semibold text-n-slate-11"
              >
                {{ contactInitials(customer.name) }}
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12"
                >
                  {{ customer.name }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{
                    [customer.email, customer.phone]
                      .filter(Boolean)
                      .join(textSeparator)
                  }}
                </span>
              </span>
            </button>
          </div>
          <p
            v-else-if="contactSearchError"
            class="mt-3 px-1 py-3 text-sm text-n-ruby-11"
          >
            {{ t('PRODUCTS.ALERT_DRAWER.CONTACT_SEARCH_ERROR') }}
          </p>
          <p
            v-else-if="!selectedCustomer && hasCustomerQuery"
            class="mt-3 px-1 py-3 text-sm text-n-slate-11"
          >
            {{ t('PRODUCTS.ALERT_DRAWER.CONTACT_SEARCH_EMPTY') }}
          </p>

          <fieldset class="mt-6">
            <legend class="mb-3 block text-sm font-medium text-n-slate-12">
              {{ t('PRODUCTS.ALERT_DRAWER.CHANNEL') }}
            </legend>
            <p class="mb-4 text-xs leading-5 text-n-slate-10">
              {{ t('PRODUCTS.ALERT_DRAWER.CONSENT_NOTE') }}
            </p>
            <div class="grid gap-2.5">
              <div
                v-for="channel in channels"
                :key="channel.id"
                :data-testid="`channel-option-${channel.id}`"
                class="rounded-xl border transition-all"
                :class="channelCardClass(channel.id)"
              >
                <label
                  class="grid cursor-pointer grid-cols-[2.25rem_minmax(0,1fr)_auto] gap-3 p-3 text-sm"
                  :class="selectedCustomer ? 'items-start' : 'items-center'"
                >
                  <input
                    :checked="isChannelSelected(channel.id)"
                    :data-testid="`channel-${channel.id}`"
                    class="peer sr-only"
                    type="checkbox"
                    :value="channel.id"
                    @change="toggleChannel(channel.id)"
                  />
                  <span
                    :data-testid="`channel-icon-${channel.id}`"
                    class="flex size-9 shrink-0 items-center justify-center rounded-full"
                    :class="channelIconBadgeClass(channel.id)"
                  >
                    <Icon :icon="channelIcon(channel.id)" class="size-5" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block min-w-0">
                      <span
                        class="block truncate text-sm font-semibold text-n-slate-12"
                      >
                        {{ channelLabel(channel.id) }}
                      </span>
                    </span>
                    <span
                      v-if="selectedCustomer && channelContactValue(channel.id)"
                      class="mt-1 block truncate text-xs text-n-slate-10"
                    >
                      {{ channelDetailLabel(channel.id) }}{{ detailSeparator
                      }}{{ channelContactValue(channel.id) }}
                    </span>
                    <span
                      v-else-if="selectedCustomer"
                      class="mt-1 flex min-w-0 items-center gap-2 text-xs"
                    >
                      <span class="truncate text-n-slate-10">
                        {{ missingChannelValueLabel(channel.id) }}
                      </span>
                      <button
                        :data-testid="`channel-value-add-${channel.id}`"
                        type="button"
                        class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium leading-4 text-n-blue-11 transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
                        @click.prevent.stop="openChannelValueEditor(channel.id)"
                      >
                        {{ t('PRODUCTS.ALERT_DRAWER.ADD_DETAIL') }}
                      </button>
                    </span>
                    <span
                      v-if="selectedCustomer"
                      class="mt-2 inline-flex max-w-full rounded-full border px-1.5 text-[10px] font-medium leading-4"
                      :class="
                        consentTagClass(
                          selectedCustomer.consent[channel.id] || 'unknown'
                        )
                      "
                    >
                      {{
                        consentLabel(
                          selectedCustomer.consent[channel.id] || 'unknown'
                        )
                      }}
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    class="flex size-5 items-center justify-center rounded border transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                    :class="[
                      selectedCustomer ? 'mt-0.5' : '',
                      isChannelSelected(channel.id)
                        ? 'border-n-brand bg-n-brand text-white'
                        : 'border-n-slate-7 bg-n-solid-1 text-transparent',
                    ]"
                  >
                    <Icon icon="i-lucide-check" class="size-3" />
                  </span>
                </label>

                <div
                  v-if="
                    selectedCustomer &&
                    !channelContactValue(channel.id) &&
                    isChannelValueEditorOpen(channel.id)
                  "
                  class="px-3 pb-3"
                >
                  <div
                    class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
                  >
                    <input
                      v-if="!isPhoneChannel(channel.id)"
                      :value="channelValueDraft(channel.id)"
                      :data-testid="`channel-value-${channel.id}`"
                      :type="channelValueInputType(channel.id)"
                      class="reset-base m-0 box-border h-8 min-h-[2rem] min-w-0 rounded-md border border-n-weak bg-n-alpha-1 px-3 py-0 text-sm leading-8 text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand focus:bg-n-solid-1 disabled:cursor-not-allowed disabled:text-n-slate-10"
                      :disabled="isChannelValueSaving(channel.id)"
                      :placeholder="channelValuePlaceholder(channel.id)"
                      @click.stop
                      @keydown.enter.prevent="
                        saveSelectedCustomerChannelValue(channel.id)
                      "
                      @input="
                        updateChannelValueDraft(channel.id, $event.target.value)
                      "
                    />
                    <PhoneNumberInput
                      v-else
                      :model-value="channelValueDraft(channel.id)"
                      :data-testid="`channel-phone-value-${channel.id}`"
                      :disabled="isChannelValueSaving(channel.id)"
                      :placeholder="channelValuePlaceholder(channel.id)"
                      class="[&>div]:!h-8 [&>div]:!bg-n-alpha-1 [&>div]:!outline-n-weak [&>div]:hover:!outline-n-slate-6 [&>div]:has-[:focus]:!outline-n-brand [&_input]:!h-8 [&_input]:!py-0"
                      @update:model-value="
                        updateChannelValueDraft(channel.id, $event)
                      "
                    />
                    <button
                      :data-testid="`channel-value-save-${channel.id}`"
                      type="button"
                      class="reset-base box-border inline-flex h-8 min-h-[2rem] shrink-0 items-center justify-center rounded-md border border-n-brand/20 bg-n-brand/10 px-3 py-0 text-xs font-medium leading-none text-n-blue-11 transition-colors hover:bg-n-brand/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:border-n-weak disabled:bg-n-alpha-1 disabled:text-n-slate-9"
                      :disabled="
                        !canSaveChannelValue(channel.id) ||
                        isChannelValueSaving(channel.id)
                      "
                      @click.stop="saveSelectedCustomerChannelValue(channel.id)"
                    >
                      {{ t('PRODUCTS.ALERT_DRAWER.SAVE_DETAIL') }}
                    </button>
                  </div>
                  <p
                    v-if="channelValueSaveErrorId === channel.id"
                    class="mt-1 text-xs text-n-ruby-10"
                  >
                    {{ t('PRODUCTS.ALERT_DRAWER.SAVE_DETAIL_ERROR') }}
                  </p>
                </div>
              </div>
            </div>
          </fieldset>
        </div>

        <footer class="flex justify-end gap-2 border-t border-n-weak px-4 py-3">
          <Button
            color="slate"
            size="sm"
            variant="ghost"
            :label="t('PRODUCTS.ALERT_DRAWER.CANCEL')"
            @click="alertDraft = null"
          />
          <Button
            data-testid="confirm-alert"
            color="blue"
            size="sm"
            :disabled="!canConfirmAlert"
            :label="t('PRODUCTS.ALERT_DRAWER.CONFIRM')"
            @click="confirmAlert"
          />
        </footer>
      </section>

      <section
        v-if="isWaitlistOpen && !isScannerOpen"
        data-testid="waitlist-drawer"
        class="flex h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl border border-n-weak bg-n-solid-1 shadow-xl lg:h-full lg:max-w-lg lg:rounded-none"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-n-weak px-4 py-4"
        >
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('PRODUCTS.WAITLIST.TITLE') }}
          </h2>
          <Button
            icon="i-lucide-x"
            color="slate"
            size="sm"
            variant="ghost"
            class="!rounded-full bg-n-alpha-2"
            @click="isWaitlistOpen = false"
          />
        </header>

        <div class="flex-1 overflow-y-auto px-4 py-2">
          <div
            v-for="product in waitlistByProduct"
            :key="product.id"
            class="border-b border-n-weak/50 py-3.5 last:border-b-0"
          >
            <div class="flex items-center gap-3">
              <img
                :src="product.image"
                :alt="product.title"
                class="size-10 shrink-0 rounded-lg object-cover bg-n-alpha-2"
              />

              <div class="min-w-0 flex-1">
                <h3 class="truncate text-sm font-medium text-n-slate-12">
                  {{ product.title }}
                </h3>
                <p
                  class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-n-slate-10"
                >
                  <span>{{ product.vendor }}</span>
                  <span class="h-3 w-px shrink-0 rounded bg-n-slate-6" />
                  <span>{{ product.sku }}</span>
                  <span class="h-3 w-px shrink-0 rounded bg-n-slate-6" />
                  <span>
                    {{
                      t('PRODUCTS.WAITLIST.ALERT_COUNT', {
                        count: product.alertCount,
                      })
                    }}
                  </span>
                </p>
              </div>
            </div>

            <div class="ml-[52px] mt-2 divide-y divide-n-weak/60">
              <div
                v-for="variant in product.variants"
                :key="variant.id"
                class="py-2"
              >
                <p class="truncate text-xs font-medium text-n-slate-11">
                  {{ variant.title }}{{ textSeparator }}{{ variant.sku }}
                </p>
                <div
                  v-for="alert in variant.alerts"
                  :key="alert.id"
                  class="mt-1 flex min-w-0 items-center gap-2 text-xs text-n-slate-10"
                >
                  <span class="truncate text-sm font-medium text-n-slate-12">
                    {{ alert.customer?.name }}
                  </span>
                  <span class="h-3 w-px shrink-0 rounded bg-n-slate-6" />
                  <span class="shrink-0">
                    {{ alertChannelLabel(alert) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <p
            v-if="!waitlistByProduct.length"
            class="py-8 text-sm text-n-slate-11"
          >
            {{ t('PRODUCTS.WAITLIST.EMPTY') }}
          </p>
        </div>
      </section>
    </div>
  </section>
</template>
